import { useState, useEffect } from 'react'
import * as Sentry from '@sentry/nextjs'
import styled from 'styled-components'
import Link from 'next/link'
import SwiperCore, { Pagination } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { breakpoint } from '../layout/styles'
import { Container } from '../styles/Container'
import Cta from '../styles/Cta'
import { Grid, Cell } from '../styles/Grid'
import { useMediaQuery } from '../helpers/mq'
import { useRouter } from 'next/router'
import { slugify } from '../helpers/string'

SwiperCore.use([Pagination])

const FeaturedProducts = ({ items: result }: any) => {
  const desktop = useMediaQuery(breakpoint.desktop)
  const { query, locale } = useRouter()
  const [items, setItems] = useState(result || [])

  // Derive a stable key from item IDs so the effect only re-runs when the
  // actual product set changes, not just because a new array reference was passed.
  const resultKey = result?.map((item: any) => item.uid || item.syndigo_id).join(',')

  useEffect(() => {
    const syndigoItems = result.filter((item: any) => item._content_type_uid === 'syndigo_product')

    if (syndigoItems.length === 0) return

    // AbortController lets us cancel in-flight fetches if the component unmounts
    // or the product set changes before the fetches complete. Without this,
    // setItems can be called on an unmounted component.
    const controller = new AbortController()
    const { signal } = controller

    const fetchItems = async () => {
      // Promise.allSettled instead of Promise.all — one failed Syndigo product
      // no longer kills the entire featured block. Valid products still render.
      const results = await Promise.allSettled(
        syndigoItems.map((item: any) =>
          fetch(
            `/api/syndigo/product?id=${item.syndigo_id}&locale=${locale === 'fr' ? 'fr-ca' : locale}&transform=true`,
            { signal }
          ).then((r) => r.ok ? r.json() : Promise.reject(r.status))
        )
      )

      if (signal.aborted) return

      const validItems = results
        .filter((r): r is PromiseFulfilledResult<any> => r.status === 'fulfilled' && r.value?.[0])
        .map((r) => r.value[0])

      if (validItems.length > 0) setItems(validItems)
    }

    fetchItems().catch((error) => {
      if (signal.aborted) return
      console.error('[FeaturedProducts] fetch error:', error)
      Sentry.captureException(error)
    })

    return () => controller.abort()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultKey, locale])

  const GridRender = (
    <Content>
      <GridContainer>
      {items
          .filter((item: any) => item.title && item.uid && item.filters?.[0]?.value)
          .slice(0, 6)
          .map((item: any) => (
            <Section key={item.uid}>
              <Link
                passHref
                href={`/products/${item.filters?.[0]?.value}/${item.uid}/${slugify(
                  item.short_title || item.title || item.gtin
                )}`}>
                <a>
                  {item.images && item.images.length > 0 && <img src={`${item.images[0].url}?size=700&filetype=webp`} alt={item.title} />}
                  {item.title && <h4>{item.title}</h4>}
                </a>
              </Link>
            </Section>
          ))}
      </GridContainer>
    </Content>
  )

  const SlideRender = (
    <Content>
      <Swiper
        pagination={{ clickable: true }}
        slidesPerView={2}
        spaceBetween={24}
        centeredSlides={false}
        breakpoints={{
          768: {
            spaceBetween: 40,
            slidesPerGroup: 1,
            centeredSlides: false
          }
        }}>
        {items
          .filter((item: any) => item.title && item.uid && item.filters?.[0]?.value)
          .slice(0, 6)
          .map((item: any) => (
            <SwiperSlide key={item.uid}>
              <Section key={item.uid}>
                <Link
                  passHref
                  href={`/products/${item.filters?.[0]?.value}/${item.uid}/${slugify(
                    item.short_title || item.title || item.gtin
                  )}`}>
                  <a>
                    {item.images && item.images.length > 0 && <img src={`${item.images[0].url}?size=700&filetype=webp`} alt={item.title} />}
                    {item.title && <h4>{item.title}</h4>}
                  </a>
                </Link>
              </Section>
            </SwiperSlide>
          ))}
      </Swiper>
    </Content>
  )

  return <Wrapper>{desktop ? GridRender : SlideRender}</Wrapper>
}

export default FeaturedProducts

const GridContainer = styled(Grid)`
  flex-wrap: wrap;
`

const Content = styled(Container)`
  margin: 0 auto;
  padding: 58px 23px;
  @media ${breakpoint.desktop} {
    padding: 0;
    max-width: 1120px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  ${Cta} {
    background: white;
    color: black;
    border: 1.25px solid #000000;
    padding: 10px 20px;
    border-radius: 2.5px;
    font-size: 16px;
    flex: 0 0 auto;
  }

  .swiper-container {
    padding-bottom: 30px;
  }

  .swiper-slide {
    height: auto;
    padding-bottom: 30px;
  }

  .swiper-pagination {
  }
`

export const Wrapper = styled(Container)`
  color: var(--text);
  max-width: 100%;
  padding: 0.1px;
  margin: 0;
`

export const Section = styled(Cell)`
  text-align: center;
  width: 100%;
  margin: 20px 0;
  height: 100%;
  background: #fff;
  cursor: pointer;
  @media ${breakpoint.desktop} {
    height: inherit;
    margin: 20px 16px;
    max-width: calc(16.6666% - 32px);
  }
  p {
    font-size: 20px;
    line-height: 26px;
    margin: 36px 0 0;
    a,
    a:visited {
      color: black;
    }
  }
  h4 {
    font-size: 14px;
    line-height: 21px;
    text-transform: none;
    font-weight: 400;
  }
  em {
    font-size: 12px;
    line-height: 1.3;
    font-style: normal;
  }
  img {
    width: 156px;
    height: 156px;
    object-fit: cover;
    margin-bottom: 15px;
    display: inline-block;
  }
  a {
    text-decoration: none;
    color: var(--text);
  }
`
